import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logo from '@/img/propsavvy-logo-with-text.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  isAuthenticatedSelector,
  setAuthTokens,
} from '@/store/auth/authSlices';
import * as Yup from 'yup';
import { Form, Formik, getIn } from 'formik';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import ResendConfirmationButton from '@/components/shared/ResendConfirmationButton/ResendConfirmationButton';
import { getUserEmailByInviteToken, signinRequest } from '@/api';
import getAxiosErrors from '@/utils/getAxiosErrors';
import './LoginPage.scss';
import { fetchProfileSuccess } from '@/store/profile/profileSlices';

const baseClass = 'LoginPage';

interface IValues {
  email: string;
  password: string;
  errorMessage?: string[];
}

const initialVal: IValues = {
  email: '',
  password: '',
  errorMessage: [],
};

const validationSchema = Yup.object().shape({
  email: Yup.string().required('Email is required').email(),
  password: Yup.string().required('Password is required').min(4),
});

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const [initialValues, setInitialValues] = useState<IValues>(initialVal);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);

  useEffect(() => {
    if (inviteToken) {
      setIsEmailLoading(true);
      getUserEmailByInviteToken({ inviteToken })
        .then((res) => {
          if (res.email) {
            setInitialValues((prev) => ({ ...prev, email: res.email }));
          }
        })
        .catch((err) => {
          console.log('loginErrors', getAxiosErrors(err));
        })
        .finally(() => {
          setIsEmailLoading(false);
        });
    }
  }, [inviteToken]);

  useEffect(() => {
    if (isAuthenticated) {
      if (inviteToken) {
        navigate(`/invite/${inviteToken}`);
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, navigate, inviteToken]);

  return (
    <div className={baseClass}>
      <div className={`${baseClass}-view`}>
        <div className={`${baseClass}-view-logo`}>
          <img src={logo} alt="logo" />
          <p className={`${baseClass}-view-logo-desc`}>
            Please enter your user information.
          </p>
        </div>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            setShowResendButton(false);
            const data = { ...values };
            signinRequest(data)
              .then((res) => {
                const { accessToken, user } = res.data;
                dispatch(setAuthTokens(accessToken));
                dispatch(fetchProfileSuccess(user));
              })
              .catch((err) => {
                console.log('--- LOGIN ERROR CATCH ---');
                console.log('Raw error object:', err);
                const errorData = getAxiosErrors(err);
                console.log('Parsed error data from getAxiosErrors:', errorData);

                // Check multiple possible locations for the error message
                const errorMessage = errorData.message || (errorData.errorMessage && errorData.errorMessage[0]) || '';
                console.log('Final error message being checked:', errorMessage);

                if (errorMessage.includes('Email is not confirmed')) { // Updated condition here
                  console.log('SUCCESS: "Email is not confirmed" detected. Setting showResendButton to true.');
                  setShowResendButton(true);
                } else {
                  console.log('FAILURE: "Email is not confirmed" NOT detected.');
                }
                setErrors(errorData);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
              values,
              errors,
              handleBlur,
              handleChange,
              touched,
              isSubmitting,
            }) => (
            <Form className={`${baseClass}-view-form`}>
              <div className={`${baseClass}-view-form-group`}>
                <Input
                  floatLabel
                  isLoading={isEmailLoading}
                  isDisabled={isEmailLoading}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  name="email"
                  type="text"
                  label="Email"
                  errors={
                    getIn(errors, 'email') && getIn(touched, 'email')
                      ? getIn(errors, 'email')
                      : ''
                  }
                />
                <Input
                  floatLabel
                  className={`${baseClass}-view-form-group-password`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  name="password"
                  type="password"
                  label="Password"
                  errors={
                    getIn(errors, 'password') && getIn(touched, 'password')
                      ? getIn(errors, 'password')
                      : ''
                  }
                />
                <div className={`${baseClass}--error`}>
                  {errors.errorMessage}
                </div>
                {showResendButton && (
                  <div className={`${baseClass}-view-form-group-resend`}>
                    <ResendConfirmationButton email={values.email} />
                  </div>
                )}
                <Button
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  type="submit"
                  className={`${baseClass}-view-form-group-submit-btn`}
                >
                  {isSubmitting ? 'Logging...' : 'Login'}
                </Button>
              </div>

              <div className={`${baseClass}-view-form-group-bottom`}>
                <Link
                  to={`/register${inviteToken ? `?inviteToken=${inviteToken}` : ''}`}
                  className={`${baseClass}-view-form-group-bottom-create`}
                >
                  Create an account
                </Link>
                <Link
                  to={'/forgot'}
                  className={`${baseClass}-view-form-group-bottom-forgot`}
                >
                  Forgot password?
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginPage;
